import React, { useEffect, useState } from "react";
import { Button, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import { ActionMenu } from "../../Common/Components/Lists/SortableList";
import { CheckMobile } from "../../AppSystem/Helpers/CheckMobile";
import { SettingsListHeader } from "../../SystemSettings/Components/SettingsListHeader";
import { VatFormPanel } from "../Components/VatFormPanel";

/**
 * AdminVatAccountList
 *
 * Invoice accounts / VAT configurations as a tab of the economic settings view
 * (replaces the former "Manage VAT" modal in AdminInvoiceList).
 * Same API endpoints and rules as before: /invoice/vat/list, /save, /update/{id}, /delete.
 */
export default function AdminVatAccountList() {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const isMobile = CheckMobile(998);

    const [vatList, setVatList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // undefined = closed, null = create, object = edit
    const [panelVat, setPanelVat] = useState(undefined);
    const [deleteVatName, setDeleteVatName] = useState(null);

    const fetchVatList = async () => {
        setIsLoading(true);
        try {
            await ApiCall({ method: "get", url: "/invoice/vat/list" }, (response) => {
                setVatList(response || []);
                setIsLoading(false);
            });
        } catch (error) {
            console.error('Error fetching VAT list:', error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchVatList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDeleteVat = async (name) => {
        setIsSubmitting(true);
        try {
            await ApiCall({ method: "delete", url: `/invoice/vat/delete?name=${encodeURIComponent(name)}` }, (response) => {
                if (response && response.success) {
                    FlashMessage(t('validationMessage.deleteSuccess') || 'VAT deleted successfully', 'flash-messageSuccess');
                    fetchVatList();
                } else {
                    FlashMessage(t('validationMessage.deleteError'), 'flash-messageError');
                }
                setIsSubmitting(false);
            });
        } catch (error) {
            console.error('Error deleting VAT:', error);
            FlashMessage(t('validationMessage.sendError') || 'Error deleting VAT', 'flash-messageError');
            setIsSubmitting(false);
        }
    };

    const renderActions = (vat) => (
        <>
            <Button
                className="bg-green-light text-green border-0 position-relative"
                onClick={() => setPanelVat(vat)}
            >
                <i className="bx bxs-pencil fs-5 align-middle pb-1" />
                <div className="tooltip-custom-button">{t("button.edit")}</div>
            </Button>
            <Button
                className="box-red border-0 position-relative"
                onClick={() => setDeleteVatName(vat.name)}
                disabled={isSubmitting}
            >
                <i className="bx bx-x-circle fs-5 align-middle pb-1" />
                <div className="tooltip-custom-button">{t("button.delete")}</div>
            </Button>
        </>
    );

    const renderMomsfritt = (vat) => (vat.momsfritt ? 'Ja' : 'Nej');

    const renderDesktopTable = () => (
        <table className="sl-table-layout economic-accordion-table">
            <colgroup>
                {/* Proportional widths so the space is spread across the columns instead of piling up in Kontonamn */}
                <col style={{ width: '20%' }} />
                <col />
                <col style={{ width: '18%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '64px' }} />
            </colgroup>
            <thead>
                <tr>
                    <th className="sl-th-base economic-accordion-th">{t('invoice.accounting') || 'Kontonummer'}</th>
                    <th className="sl-th-base economic-accordion-th">{t('invoice.accountName')}</th>
                    <th className="sl-th-base economic-accordion-th">{t('invoice.momsfritt') || 'Momsfritt'}</th>
                    <th className="sl-th-base economic-accordion-th economic-accordion-th-value">{t('invoice.vatValue')}</th>
                    <th className="sl-th-base economic-accordion-th" />
                </tr>
            </thead>
            <tbody>
                {vatList.map((vat, index) => (
                    <tr key={vat.id || index} className={`sl-row ${index < vatList.length - 1 ? 'sl-row-divider' : ''}`}>
                        <td className="sl-td-base economic-accordion-td">{vat.accounting || '-'}</td>
                        <td className="sl-td-base economic-accordion-td">{vat.name}</td>
                        <td className="sl-td-base economic-accordion-td">{renderMomsfritt(vat)}</td>
                        <td className="sl-td-base economic-accordion-td economic-accordion-value">{`${vat.vatValue}%`}</td>
                        <td className="sl-td-base economic-accordion-td">
                            <div className="sl-td-action-wrap">
                                <ActionMenu item={vat} actionColumn={() => renderActions(vat)} iconType="bx-dots-horizontal-rounded" />
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderMobileCards = () => (
        <div className="reimbursement-cards-list">
            {vatList.map((vat, index) => (
                <div key={vat.id || index} className="reimbursement-card-item">
                    <div className="reimbursement-card-content-row">
                        <div className="reimbursement-card-left">
                            <div className="reimbursement-card-org">
                                {`${t('invoice.accounting') || 'Kontonummer'}: ${vat.accounting || '-'}`}
                            </div>
                            <div className="reimbursement-card-purpose">{vat.name}</div>
                            <div className="reimbursement-card-org">
                                {`${t('invoice.momsfritt') || 'Momsfritt'}: ${renderMomsfritt(vat)}`}
                            </div>
                        </div>
                        <div className="reimbursement-card-right">
                            <div className="reimbursement-card-right-top">
                                <div className="reimbursement-card-amount reimbursement-card-amount-positive">{`${vat.vatValue}%`}</div>
                                <div className="reimbursement-card-actions-slot">
                                    <ActionMenu item={vat} actionColumn={() => renderActions(vat)} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="mb-5 p-0 position-relative sl-wrap economic-settings">
            <SettingsListHeader
                createText={t('invoice.addNewVat')}
                onCreate={() => setPanelVat(null)}
            />

            <div className={isMobile ? "" : "sl-card-shell"}>
                {!isMobile && (
                    <div className="economic-accordion-header" style={{ cursor: 'default' }}>
                        <span className="economic-accordion-title">{t('invoice.existingVatConfigurations')}</span>
                    </div>
                )}
                <div className={isMobile ? "" : "economic-accordion-body"}>
                    {isLoading ? (
                        <div className="text-center py-4">
                            <Spinner color="success" />
                        </div>
                    ) : vatList.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            {t('invoice.noVatConfigurationsFound') || 'No VAT configurations found.'}
                        </div>
                    ) : (
                        isMobile ? renderMobileCards() : renderDesktopTable()
                    )}
                </div>
            </div>

            <VatFormPanel
                isOpen={panelVat !== undefined}
                vat={panelVat || null}
                onClose={() => setPanelVat(undefined)}
                onSaved={fetchVatList}
            />

            <CustomModal
                isOpen={deleteVatName !== null}
                toggle={() => setDeleteVatName(null)}
                body={<div>{`${t('invoice.deleteVatConfirmation')} "${deleteVatName}"?`}</div>}
                footer={
                    <>
                        <Button className="box-green border-0" onClick={() => { const name = deleteVatName; setDeleteVatName(null); handleDeleteVat(name); }}>
                            {t('button.delete')}
                        </Button>
                        <Button className="box-red border-0" onClick={() => setDeleteVatName(null)}>
                            {t('button.cancel')}
                        </Button>
                    </>
                }
            />
        </div>
    );
}
